import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useLicenseRules } from '@/hooks';

interface LicenseObligationsModalProps {
  licenseId: number | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const RULE_COLUMNS: { key: 'can' | 'cant' | 'must'; title: string; icon: typeof CheckCircle; iconClass: string }[] = [
  { key: 'can', title: 'Can', icon: CheckCircle, iconClass: 'text-green-600 dark:text-green-400' },
  { key: 'cant', title: "Can't", icon: XCircle, iconClass: 'text-red-600 dark:text-red-400' },
  { key: 'must', title: 'Must', icon: AlertTriangle, iconClass: 'text-orange-600 dark:text-orange-400' },
];

export function LicenseObligationsModal({ licenseId, isOpen, onOpenChange }: LicenseObligationsModalProps) {
  const { data, loading, error } = useLicenseRules(licenseId, isOpen);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            {data?.license.spdxfullname || data?.license.spdxidentifier || 'License Obligations'}
          </DialogTitle>
          <DialogDescription className="text-base mt-2">
            {data?.license.licensesummary || 'No summary available'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-muted-foreground text-sm">Loading obligations...</div>
        )}

        {error && (
          <div className="py-8 text-center text-red-600 dark:text-red-400 text-sm">{error}</div>
        )}

        {!loading && !error && data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-4">
            {RULE_COLUMNS.map(({ key, title, icon: Icon, iconClass }) => (
              <Card key={key}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${iconClass}`} />
                    {title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  {data.rules[key].length > 0 ? (
                    <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                      {data.rules[key].map((rule, i) => (
                        <li key={i}>{rule}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground italic">No rules defined</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
